/* eslint-disable jsx-a11y/alt-text */
import primeinfra from "../assets/primeinfra.png";
import goldHeart from "../assets/goldheart.png";
import ictsi from "../assets/ictsi.jpg";
import hapagLloyd from "../assets/hapag-lloyd.jpeg";
import ictsiNew from "../assets/ictsi-new.png";
import pmp from "../assets/pmp.png";
import cnlc from "../assets/cnlc.png";
import alrimah from "../assets/alrimah.png";

import { Chip } from "./ServicesSection";
import { SectionHeader } from "./SectionHeader";
import { Reveal } from "./Reveal";
import { useLang } from "../i18n/LanguageContext";

// Bilingual company names shown under each logo. The matching language renders
// per mode; proper nouns (CNLC, ICTSI, RIPE…) stay untranslated by design.
type Logo = { src: string; nameEn: string; nameAr: string; href?: string };

const logos: Logo[] = [
  {
    src: primeinfra,
    nameEn: "Prime Infra",
    nameAr: "شركة برايم إنفرا",
    href: "https://primeinfra.ph/portfolio/prime-power-energy-solutions/",
  },
  {
    src: goldHeart,
    nameEn: "Gold Heart",
    nameAr: "شركة القلب الذهبي",
    href: "https://goldheart-int.com",
  },
  {
    src: ictsi,
    nameEn: "ICTSI Iraq",
    nameAr: "شركة ICTSI العراق",
    href: "https://www.ictsiiraq.com/",
  },
  {
    src: hapagLloyd,
    nameEn: "Hapag-Lloyd",
    nameAr: "شركة Hapag-Lloyd",
    href: "https://www.hapag-lloyd.com/",
  },
  {
    src: ictsiNew,
    nameEn: "ICTSI Philippines",
    nameAr: "شركة ICTSI الفلبينية",
    href: "https://www.ictsi.com/",
  },
  {
    src: pmp,
    nameEn: "Prime Metro Power",
    nameAr: "شركة برايم مترو باور",
  },
  {
    src: cnlc,
    nameEn: "CNLC — China National Logging Corporation",
    nameAr: "شركة CNLC - الجس الوطنية الصينية",
    href: "http://www.cnlc.cn/",
  },
  {
    src: alrimah,
    nameEn: "Al Rimah Emirati — RIPE",
    nameAr: "شركة الرماح الاماراتية RIPE",
  },
];

// Bilingual client names — the matching language shows per mode. English forms
// for the Arabic-only entries are best-effort transliterations; confirm them.
type Client = { en: string; ar: string };

const clients: Client[] = [
  { en: "Al-Takhasusia Company", ar: "شركة التخصصية" },
  { en: "Al-Arjuan Oil", ar: "زيت الارجوان" },
  { en: "Engineering Technology Company", ar: "الشركة التقنية الهندسية" },
  { en: "BGT — Basra Gateway Terminal", ar: "شركة BGT - بوابة البصرة" },
  { en: "Majid Mozan Company", ar: "شركة ماجد موزان" },
  { en: "Larsa Company", ar: "شركة لارسا" },
  { en: "Arabian Navigation", ar: "العربية للملاحة" },
  { en: "And many other local companies", ar: "والعديد من الشركات المحلية الأخرى" },
];

export function NewClients() {
  const { t, lang } = useLang();
  return (
    <section
      id="clients"
      className="relative border-t border-primary/10 px-6 lg:px-24"
    >
      <div className="container mx-auto py-16 lg:py-24">
        <SectionHeader
          eyebrow={t.clients.eyebrow}
          title={t.clients.title}
          align="center"
          className="mx-auto"
        />

        {/* Logo wall */}
        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {logos.map((logo, i) => (
            <Reveal key={logo.nameEn} delay={(i % 4) * 0.06}>
              <LogoTile
                src={logo.src}
                href={logo.href}
                name={lang === "ar" ? logo.nameAr : logo.nameEn}
              />
            </Reveal>
          ))}
        </div>

        {/* Company-name chips */}
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {clients.map((client, i) => (
            <Reveal key={client.en} delay={(i % 3) * 0.06}>
              <Chip text={lang === "ar" ? client.ar : client.en} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function LogoTile({
  src,
  name,
  href,
}: {
  src: string;
  name: string;
  href?: string;
}) {
  const tile = (
    <div className="flex h-full min-h-[9.5rem] flex-col items-center justify-center gap-3 rounded-xl border border-primary/10 bg-white p-4 text-center transition-all duration-300 hover:-translate-y-1 hover:border-secondary/60 hover:shadow-[0_18px_40px_rgba(5,37,45,0.1)]">
      <div className="flex h-16 items-center justify-center">
        <img src={src} alt={name} className="max-h-full max-w-full object-contain" />
      </div>
      <p className="font-serif text-sm font-medium leading-snug text-primary/80">
        {name}
      </p>
    </div>
  );

  if (!href) return tile;
  return (
    <a href={href} target="_blank" rel="noreferrer" aria-label={name}>
      {tile}
    </a>
  );
}
